import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ImageLightbox from '../components/blocks/ImageLightbox';
import PostBodyMarkdown from '../components/blocks/PostBodyMarkdown';
import InlineMarkdown, { stripInlineMarkdown } from '../components/blocks/InlineMarkdown';
import DeletePostConfirmModal from '../components/blocks/DeletePostConfirmModal';
import { useAuth, isFounder } from '../contexts/AuthContext';

const PressReleaseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const canManage = isFounder(currentUser);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState('archive');
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState(null);
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0, alt: '' });

  const lang = useMemo(() => {
    const raw = (i18n.language || 'en').toLowerCase();
    if (raw.startsWith('tc')) return 'tc';
    if (raw.startsWith('sc')) return 'sc';
    return 'en';
  }, [i18n.language]);

  const pickLocalized = (fieldBase) => {
    if (!release) return '';
    const key = `${fieldBase}_${lang}`;
    return release[key] || release[fieldBase] || '';
  };

  useEffect(() => {
    const fetchRelease = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const url =
          canManage && token
            ? `/api/press-releases/${slug}?includeArchived=true`
            : `/api/press-releases/${slug}`;
        const response = await fetch(url, {
          ...(canManage && token ? { headers: { Authorization: `Bearer ${token}` } } : {})
        });
        if (!response.ok) {
          throw new Error(`Failed to load press release (${response.status})`);
        }
        const data = await response.json();
        setRelease(data);
        setError(null);
      } catch (err) {
        console.error('Error loading press release:', err);
        setError(err.message || 'Failed to load press release');
      } finally {
        setLoading(false);
      }
    };

    fetchRelease();
  }, [slug, canManage]);

  const handleRestoreRelease = async () => {
    if (!release?.slug || restoring) return;
    try {
      setRestoring(true);
      setRestoreError(null);
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/press-releases/${encodeURIComponent(release.slug)}/restore`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || t('pressReleases.restoreError'));
      }
      setRelease((prev) => (prev ? { ...prev, archived: false, ...(data?.data || {}) } : prev));
    } catch (err) {
      console.error('Restore error:', err);
      setRestoreError(err.message || t('pressReleases.restoreError'));
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const images = useMemo(() => {
    if (!release) return [];
    if (Array.isArray(release.images) && release.images.length > 0) return release.images;
    if (release.heroImageUrl) return [release.heroImageUrl];
    return [];
  }, [release]);

  if (loading) {
    return (
      <div className="bg-canvas min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-surface rounded-lg shadow-sm p-6 text-center">
            <p className="text-ink-muted">
              {t('pressReleases.loading', 'Loading press release...')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !release) {
    return (
      <div className="bg-canvas min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-surface rounded-lg shadow-sm p-6">
            <button
              onClick={() => navigate(-1)}
              className="text-sm text-link hover:text-link-hover mb-4"
            >
              ← {t('pressReleases.backToList', 'Back to press releases')}
            </button>
            <p className="text-red-700">
              {t('pressReleases.error', 'Unable to load press release. Please try again later.')}
            </p>
            {error && (
              <p className="text-red-500 text-xs mt-1">
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const title = pickLocalized('title');
  const body = pickLocalized('body');
  const titleAltForMedia = stripInlineMarkdown(release.heroImageAlt || title);

  return (
    <div className="bg-canvas min-h-screen">
      <DeletePostConfirmModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        variant="press"
        mode={deleteMode}
        slug={release.slug}
        itemTitle={title}
        onSuccess={() => navigate('/press-releases')}
      />
      <ImageLightbox
        isOpen={lightbox.open}
        images={lightbox.images}
        index={lightbox.index}
        alt={lightbox.alt}
        onClose={() => setLightbox({ open: false, images: [], index: 0, alt: '' })}
        onPrev={() =>
          setLightbox((prev) => ({
            ...prev,
            index: (prev.index - 1 + (prev.images?.length || 1)) % (prev.images?.length || 1)
          }))
        }
        onNext={() =>
          setLightbox((prev) => ({
            ...prev,
            index: (prev.index + 1) % (prev.images?.length || 1)
          }))
        }
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <button
            type="button"
            onClick={() => navigate('/press-releases')}
            className="text-sm text-link hover:text-link-hover"
          >
            ← {t('pressReleases.backToList', 'Back to press releases')}
          </button>
          {canManage && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate('/press-releases', { state: { openEditSlug: slug } })}
                className="text-sm font-medium px-3 py-1.5 rounded-md border border-edge-strong text-ink bg-surface hover:bg-surface-muted"
              >
                {t('pressReleases.admin.editPost')}
              </button>
              {release.archived ? (
                <>
                <button
                  type="button"
                  disabled={restoring}
                  onClick={handleRestoreRelease}
                  className="text-sm font-medium px-3 py-1.5 rounded-md border border-emerald-500 text-emerald-800 bg-surface hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-emerald-500 dark:text-emerald-200 dark:hover:bg-emerald-950/40"
                >
                  {restoring ? t('pressReleases.restoring') : t('pressReleases.restorePost')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteMode('remove');
                    setDeleteOpen(true);
                  }}
                  className="text-sm font-medium px-3 py-1.5 rounded-md border border-danger text-danger bg-surface hover:bg-danger/10"
                >
                  {t('deleteConfirm.removeButton')}
                </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setDeleteMode('archive');
                    setDeleteOpen(true);
                  }}
                  className="text-sm font-medium px-3 py-1.5 rounded-md border border-amber-400 text-amber-800 bg-surface hover:bg-amber-50 dark:border-amber-500 dark:text-amber-200 dark:hover:bg-amber-950/40"
                >
                  {t('deleteConfirm.deleteButton')}
                </button>
              )}
            </div>
          )}
        </div>

        {restoreError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-300">
            {restoreError}
          </div>
        )}

        {release.archived && canManage && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-100">
            {t('pressReleases.archivedBanner')}
          </div>
        )}

        <article
          className={`bg-surface rounded-lg shadow-sm overflow-hidden border ${
            release.archived && canManage ? 'border-amber-200' : 'border-edge'
          }`}
        >
          {images.length > 0 && (
            <div className="w-full bg-surface-muted overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-[1px]">
                {images.map((src, index) => (
                  <div
                    key={src || index}
                    className="h-48 md:h-56 bg-surface-muted overflow-hidden group"
                    role="button"
                    tabIndex={0}
                    onClick={() => setLightbox({ open: true, images, index, alt: titleAltForMedia })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setLightbox({ open: true, images, index, alt: titleAltForMedia });
                      }
                    }}
                  >
                    <img
                      src={src}
                      alt={titleAltForMedia}
                      className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] group-active:scale-[1.05]"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-ink">
                <InlineMarkdown variant="heading">{title}</InlineMarkdown>
              </h1>
              {release.publishedAt && (
                <time
                  dateTime={release.publishedAt}
                  className="text-sm text-ink-subtle"
                >
                  {formatDate(release.publishedAt)}
                </time>
              )}
            </div>

            {body && <PostBodyMarkdown>{body}</PostBodyMarkdown>}

            <div className="flex flex-wrap gap-3 mb-3">
              {release.linkedinUrl && (
                <a
                  href={release.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary-hover"
                >
                  {t('pressReleases.viewOnLinkedIn', 'View on LinkedIn')}
                </a>
              )}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default PressReleaseDetail;
